import { useEffect, useMemo, useState, useCallback } from "react";
import { DiagnosticContextFields } from "@/components/DiagnosticContextFields";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, ClipboardList, Save, Cloud } from "lucide-react";
import { api } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import { TESTIDS } from "@/constants/testIds";
import { extractErrorMessage } from "@/lib/format";

const INTRO_PARAGRAPHS = [
  `Vamos a evaluar el estado actual de la gestión del talento en tu empresa, analizando 6 áreas clave: Marca empleadora y atracción de talento, Selección y contratación, Onboarding e integración, Evaluación del desempeño, Feedback y comunicación interna, y Desarrollo profesional y retención.`,
  `A continuación encontrarás 30 preguntas sobre prácticas habituales en cada una de estas áreas. Cada pregunta tiene 4 respuestas posibles que describen situaciones reales — selecciona la que mejor refleje cómo funciona hoy tu empresa, no cómo te gustaría que funcionase.`,
  `Completar el diagnóstico lleva entre 12 y 15 minutos. Al terminar recibirás tu puntuación por área, tu puntuación total y una interpretación detallada de los resultados.`,
];

// Reframe honesto: no es un examen, es un espejo. Reduce ansiedad evaluativa
// y desincentiva respuestas infladas por deseabilidad social.
const HONESTY_REFRAME = {
  title: "No es un examen, es un espejo",
  bullets: [
    "Este diagnóstico no evalúa a tu empresa: te da un mapa para decidir dónde invertir. Cuanto más honestas sean tus respuestas, más útil será el resultado.",
    "Nadie más verá tus respuestas individuales — solo tú y quien decidas invitar.",
    "Responder lo que 'debería ser' genera un plan genérico. Responder con crudeza genera un plan accionable para tu realidad concreta.",
  ],
};

export default function Diagnostic() {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [ctx, setCtx] = useState({ companyName: "", teamSize: "" });
  const [pageIdx, setPageIdx] = useState(-1); // -1 = intro, 0..5 = areas, 6 = review
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null); // timestamp del último autosave OK
  const [savedTick, setSavedTick] = useState(0); // fuerza re-render del indicador cada 15s

  useEffect(() => {
    (async () => {
      try {
        const [qResp, draftResp] = await Promise.all([
          api.get("/diagnostic/questions"),
          api.get("/diagnostic/draft"),
        ]);
        setAreas(qResp.data.areas);
        setQuestions(qResp.data.questions);

        const draft = draftResp.data?.draft;
        if (draft) setCtx({ companyName: draft.company_name || "", teamSize: draft.team_size || "" });
        if (draft && draft.answers?.length > 0) {
          const restored = {};
          for (const a of draft.answers) restored[a.n] = a.selected;
          setAnswers(restored);
          setHasDraft(true);
          // Salta a la primera sección con preguntas pendientes
          const areaOrder = qResp.data.areas;
          const qs = qResp.data.questions;
          let jumpTo = 0;
          for (let i = 0; i < areaOrder.length; i++) {
            const areaQs = qs.filter((q) => q.area === areaOrder[i].code);
            const allAnswered = areaQs.every((q) => restored[q.n] !== undefined);
            if (!allAnswered) {
              jumpTo = i;
              break;
            }
            if (i === areaOrder.length - 1) jumpTo = i;
          }
          setPageIdx(jumpTo);
          toast.success(
            `Retomamos tu diagnóstico: ${draft.answered_count} de ${draft.total_questions} preguntas respondidas.`
          );
        }
      } catch (_e) {
        toast.error("No pudimos cargar el diagnóstico");
        navigate("/dashboard");
      } finally {
        setDraftLoaded(true);
      }
    })();
  }, [navigate]);

  const answersArray = useMemo(
    () =>
      Object.entries(answers).map(([n, selected]) => ({
        n: parseInt(n, 10),
        selected,
      })),
    [answers]
  );

  const saveDraft = useCallback(
    async (silent = false) => {
      if (!draftLoaded) return; // evita guardar antes de cargar
      setSavingDraft(true);
      try {
        await api.post("/diagnostic/draft/save", {
          answers: answersArray,
          company_name: ctx.companyName.trim() || null,
          team_size: ctx.teamSize || null,
        });
        setHasDraft(true);
        setLastSavedAt(Date.now());
        if (!silent) toast.success("Progreso guardado. Puedes retomarlo cuando quieras.");
        return true;
      } catch (err) {
        if (!silent) {
          toast.error(extractErrorMessage(err, "No pudimos guardar el progreso"));
        }
        return false;
      } finally {
        setSavingDraft(false);
      }
    },
    [answersArray, draftLoaded, ctx]
  );

  // Autoguardado silencioso: cada vez que el usuario cambia una respuesta
  // esperamos 1.5s de "quietud" y guardamos. Debounce clásico.
  useEffect(() => {
    if (!draftLoaded || answersArray.length === 0 || pageIdx < 0) return undefined;
    const handle = setTimeout(() => {
      saveDraft(true);
    }, 1500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, draftLoaded, pageIdx]);

  // Refresca el "hace Xs" cada 15s para que el indicador no quede congelado.
  useEffect(() => {
    if (!lastSavedAt) return undefined;
    const t = setInterval(() => setSavedTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, [lastSavedAt]);

  const savedLabel = useMemo(() => {
    if (!lastSavedAt) return null;
    // savedTick provoca re-render; el cálculo real usa Date.now()
    void savedTick;
    const s = Math.round((Date.now() - lastSavedAt) / 1000);
    if (s < 5) return "Guardado hace un instante";
    if (s < 60) return `Guardado hace ${s}s`;
    const m = Math.floor(s / 60);
    return `Guardado hace ${m} min`;
  }, [lastSavedAt, savedTick]);

  async function saveAndExit() {
    if (answersArray.length === 0) {
      navigate("/dashboard");
      return;
    }
    const ok = await saveDraft(true);
    if (ok) {
      toast.success("Progreso guardado. Continúa cuando quieras desde tu dashboard.");
      setTimeout(() => navigate("/dashboard"), 400);
    }
  }

  const currentArea = areas[pageIdx];
  const currentQuestions = useMemo(
    () => (currentArea ? questions.filter((q) => q.area === currentArea.code) : []),
    [currentArea, questions]
  );

  const answered = Object.keys(answers).length;
  const progress = Math.round((answered / 30) * 100);

  function selectAnswer(qn, optionIdx) {
    setAnswers((prev) => ({ ...prev, [qn]: optionIdx }));
  }

  function next() {
    for (const q of currentQuestions) {
      if (answers[q.n] === undefined) {
        toast.error("Responde todas las preguntas de esta sección antes de continuar");
        return;
      }
    }
    if (pageIdx < areas.length - 1) {
      setPageIdx(pageIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      submit();
    }
  }

  function prev() {
    if (pageIdx > 0) {
      setPageIdx(pageIdx - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  async function submit() {
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([n, selected]) => ({
          n: parseInt(n, 10),
          selected,
        })),
        company_name: ctx.companyName.trim() || null,
        team_size: ctx.teamSize || null,
      };
      const { data } = await api.post("/diagnostic/submit", payload);
      setHasDraft(false);
      toast.success("Diagnóstico enviado. Te llevamos a tus resultados.");
      navigate(`/results/${data.id}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "No pudimos enviar el diagnóstico"));
    } finally {
      setSubmitting(false);
    }
  }

  if (!areas.length) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <BrandHeader />
        <div className="max-w-3xl mx-auto p-12 text-gray-500">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid={TESTIDS.diagnostic.root}>
      <BrandHeader
        right={
          <div className="flex items-center gap-2">
            {savedLabel && pageIdx >= 0 && (
              <span
                className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full"
                data-testid="diagnostic-saved-indicator"
                title={savedLabel}
              >
                <Cloud className="w-3 h-3" strokeWidth={2} />
                {savingDraft ? "Guardando…" : savedLabel}
              </span>
            )}
            {answersArray.length > 0 && pageIdx >= 0 && (
              <button
                className="btn-ghost text-sm"
                onClick={() => saveDraft(false)}
                disabled={savingDraft}
                data-testid="diagnostic-save-draft-btn"
              >
                <Save className="w-3.5 h-3.5" strokeWidth={1.75} />
                {savingDraft ? "Guardando…" : "Guardar"}
              </button>
            )}
            <button
              className="btn-ghost"
              onClick={saveAndExit}
              disabled={savingDraft}
              data-testid="diagnostic-save-exit-btn"
            >
              Guardar y salir
            </button>
          </div>
        }
      />

      {/* Progress bar */}
      {pageIdx >= 0 && (
        <div className="sticky top-16 z-20 bg-white/85 backdrop-blur-md border-b border-slate-100">
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-3.5">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span className="flex items-center gap-2">
                <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#0f6e56]">
                  Sección {pageIdx + 1} de {areas.length}
                </span>
                <span className="text-slate-300">·</span>
                <span className="font-medium text-slate-800">{currentArea?.name}</span>
              </span>
              <span className="flex items-center gap-2">
                {savedLabel && (
                  <span
                    className="md:hidden inline-flex items-center gap-1 text-[10px] text-emerald-700"
                    data-testid="diagnostic-saved-indicator-mobile"
                  >
                    <Cloud className="w-3 h-3" strokeWidth={2} />
                    {savingDraft ? "Guardando…" : savedLabel.replace("Guardado ", "")}
                  </span>
                )}
                <span className="font-medium text-slate-700">
                  {answered}/30 · {progress}%
                </span>
              </span>
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full brand-gradient transition-all duration-500"
                style={{ width: `${progress}%` }}
                data-testid={TESTIDS.diagnostic.progressBar}
              />
            </div>
            {/* Step-dots por sección: refuerza el progreso tangible. */}
            <div className="flex items-center gap-1.5 mt-3">
              {areas.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i < pageIdx ? "w-6 bg-[#0f6e56]" : i === pageIdx ? "w-6 bg-[#0f6e56]" : "w-6 bg-slate-200"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {pageIdx === -1 && (
          <div className="fade-in-up">
            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0f6e56] mb-4">
              <ClipboardList className="w-4 h-4" strokeWidth={2} /> Antes de empezar
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              Diagnóstico de gestión del talento
            </h1>
            <p className="text-gray-600 text-base mt-6 leading-relaxed">{INTRO_PARAGRAPHS[0]}</p>
            <p className="text-gray-600 text-base mt-4 leading-relaxed">{INTRO_PARAGRAPHS[1]}</p>
            <p className="text-gray-600 text-base mt-4 leading-relaxed">{INTRO_PARAGRAPHS[2]}</p>

            {/* Reframe: no es un examen, es un espejo. Anti-gaming sutil. */}
            <div
              className="mt-8 rounded-2xl border p-5 md:p-6 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, rgba(15,110,86,0.05) 0%, rgba(201,168,76,0.06) 100%)",
                borderColor: "rgba(15,110,86,0.15)",
              }}
              data-testid="diagnostic-honesty-reframe"
            >
              <span
                aria-hidden
                className="absolute top-0 left-0 bottom-0 w-1"
                style={{
                  background:
                    "linear-gradient(180deg, #C9A84C 0%, #E5C874 50%, #0F6E56 100%)",
                }}
              />
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: "#0F6E56", color: "#E5C874" }}
                >
                  <CheckCircle2 className="w-5 h-5" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#0f6e56] mb-1">
                    Antes de empezar
                  </div>
                  <h3 className="font-display text-lg md:text-xl font-semibold text-slate-900 mb-2 leading-tight">
                    {HONESTY_REFRAME.title}
                  </h3>
                  <ul className="space-y-2">
                    {HONESTY_REFRAME.bullets.map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-[13.5px] leading-relaxed text-slate-700"
                      >
                        <span
                          aria-hidden
                          className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ background: "#C9A84C" }}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
              {areas.map((a, i) => (
                <div
                  key={a.code}
                  className="rounded-xl border border-gray-100 bg-white p-4 flex items-start gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#0f6e56]/8 text-[#0f6e56] font-bold flex items-center justify-center text-sm font-display">
                    {a.code}
                  </div>
                  <div className="text-sm text-gray-700 font-medium leading-snug pt-0.5">
                    {a.name}
                  </div>
                </div>
              ))}
            </div>

            <DiagnosticContextFields
              companyName={ctx.companyName}
              teamSize={ctx.teamSize}
              onChange={(patch) => setCtx((c) => ({ ...c, ...patch }))}
            />

            <div className="mt-10 flex justify-end">
              <button
                className="btn-primary"
                onClick={() => setPageIdx(0)}
                data-testid={TESTIDS.diagnostic.startBtn}
              >
                Empezar diagnóstico <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {pageIdx >= 0 && currentArea && (
          <div className="fade-in-up" key={pageIdx}>
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0f6e56] mb-3">
              Área {currentArea.code} · Sección {pageIdx + 1} de {areas.length}
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              {currentArea.name}
            </h2>
            <p className="text-gray-500 mt-3 italic text-[15px] leading-relaxed">
              {currentArea.description}
            </p>

            <div className="mt-10 space-y-8">
              {currentQuestions.map((q) => (
                <div
                  key={q.n}
                  className="rounded-3xl border border-slate-200/70 bg-white p-6 md:p-8 shadow-[0_2px_16px_-4px_rgba(15,23,42,0.04)]"
                >
                  {/* Cabecera pregunta: círculo numerado + título + nota */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-9 h-9 rounded-full bg-[#0f6e56]/8 text-[#0f6e56] flex items-center justify-center font-display font-semibold text-sm flex-shrink-0">
                      {String(q.n).padStart(2, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-[20px] md:text-[22px] font-semibold text-slate-900 leading-snug tracking-tight">
                        {q.text}
                      </h3>
                      {q.note && (
                        <p
                          className="text-[13px] text-slate-500 mt-2 leading-relaxed"
                          data-testid={`diagnostic-note-${q.n}`}
                        >
                          {q.note}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    {q.options.map((opt, idx) => {
                      const isSelected = answers[q.n] === idx;
                      return (
                        <label
                          key={idx}
                          className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                            isSelected
                              ? "border-2 border-[#0f6e56] bg-[#0f6e56]/5"
                              : "border border-slate-200 hover:border-[#0f6e56]/40"
                          }`}
                          data-testid={`${TESTIDS.diagnostic.option}-${q.n}-${idx}`}
                        >
                          <input
                            type="radio"
                            name={`q-${q.n}`}
                            checked={isSelected}
                            onChange={() => selectAnswer(q.n, idx)}
                            className="sr-only"
                          />
                          {/* Radio custom: círculo relleno cuando está seleccionado */}
                          <span
                            className={`mt-0.5 w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${
                              isSelected
                                ? "border-2 border-[#0f6e56]"
                                : "border-2 border-slate-300"
                            }`}
                            aria-hidden="true"
                          >
                            {isSelected && (
                              <span className="w-2 h-2 rounded-full bg-[#0f6e56]"></span>
                            )}
                          </span>
                          <div className="flex-1">
                            <div
                              className={`text-[14.5px] leading-relaxed ${
                                isSelected ? "text-slate-900 font-medium" : "text-slate-700"
                              }`}
                            >
                              {opt}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center justify-between gap-3">
              <button
                onClick={prev}
                disabled={pageIdx === 0}
                className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={TESTIDS.diagnostic.prevBtn}
              >
                <ArrowLeft className="w-4 h-4" /> Anterior
              </button>
              <button
                onClick={next}
                disabled={submitting}
                className="btn-primary"
                data-testid={
                  pageIdx === areas.length - 1
                    ? TESTIDS.diagnostic.submitBtn
                    : TESTIDS.diagnostic.nextBtn
                }
              >
                {submitting
                  ? "Enviando…"
                  : pageIdx === areas.length - 1
                    ? "Finalizar diagnóstico"
                    : "Siguiente sección"}{" "}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
